/* RESPONSIBLE TEAM: team-standalone */
import BaseValidator from 'ember-cp-validations/validators/base';
import { inject as service } from '@ember/service';
import type Model from '@ember-data/model';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import type { HandoffToSalesforceOwnerIdByIntegration } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';

export default class HandoffToSalesforceOwnerIdValid extends BaseValidator {
  @service declare intl: IntlService;

  async validate(
    _value: unknown,
    options: ValidationOptions & { messageKey: string; allowUnassigned?: boolean },
    model: Model & {
      handoffToSalesforceOwnerId: string | undefined;
      handoffToSalesforceOwnerIdByIntegration?: HandoffToSalesforceOwnerIdByIntegration;
      finStandaloneService?: FinStandaloneService;
    },
  ) {
    let finStandaloneService = model.finStandaloneService;

    if (!finStandaloneService?.salesforcePlatform) {
      if (options.allowUnassigned && !model.handoffToSalesforceOwnerId) {
        return true;
      }
      return this.intl.t(options.messageKey);
    }

    await this.waitForSalesforceSetupData(finStandaloneService);

    let platform = finStandaloneService.salesforcePlatform;
    let configurations = (platform.configurations || []).slice();
    let handoffByIntegration = model.handoffToSalesforceOwnerIdByIntegration || [];

    // When configurations and per-integration entries exist, defer to the
    // handoff-to-salesforce-owner-id-by-integration-valid validator.
    if (configurations.length > 0 && handoffByIntegration.length > 0) {
      return true;
    }

    if (!model.handoffToSalesforceOwnerId) {
      if (options.allowUnassigned) {
        return true;
      }
      return this.intl.t(options.messageKey);
    }

    if (configurations.length > 0) {
      for (let config of configurations) {
        let handoverOption = config.findHandoverOptionById(model.handoffToSalesforceOwnerId);
        if (handoverOption) {
          return true;
        }
      }
      return this.intl.t(options.messageKey);
    }

    let setupData = platform.salesforceSetupData;
    if (!setupData) {
      return this.intl.t(options.messageKey);
    }
    let { admins = [], queues = [] } = setupData;
    let handOffOptions = [...admins.mapBy('id'), ...queues.mapBy('id')];
    if (handOffOptions.includes(model.handoffToSalesforceOwnerId)) {
      return true;
    }

    return this.intl.t(options.messageKey);
  }

  static getDependentsFor() {
    return [
      'model.handoffToSalesforceOwnerId',
      'model.handoffToSalesforceOwnerIdByIntegration',
      'model.finStandaloneService.salesforcePlatform.salesforceSetupData',
      'model.finStandaloneService.salesforcePlatform.configurationData',
    ];
  }

  private async waitForSalesforceSetupData(finStandaloneService: FinStandaloneService) {
    let dataLoader = finStandaloneService.salesforcePlatform?.salesforceDataLoader;
    if (!dataLoader) {
      return;
    }

    let task = taskFor(dataLoader.loadSalesforceSetupData);
    if (!task.isRunning) {
      return;
    }

    return task.last!;
  }
}
